import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface OpenLibraryResetStats {
  updated_count: number;
  books_with_no_synopsis: number;
  books_with_no_series: number;
  books_with_no_pages: number;
}

/**
 * Phase 2: Marks all OpenLibrary books with incomplete data as pending for re-enrichment
 * This targets the 90,405+ books that were marked as "complete" but lack quality data
 */
export async function resetOpenLibraryEnrichmentStatus(): Promise<OpenLibraryResetStats> {
  try {
    console.log('[OpenLibraryReset] Starting Phase 2: Marking OpenLibrary books for re-enrichment...');
    
    toast.info('Analyzing OpenLibrary books...', {
      description: 'This will mark books with incomplete data for re-enrichment'
    });

    // Call the database function to reset enrichment status
    const { data, error } = await supabase.rpc('reset_openlibrary_enrichment_status');

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('No data returned from reset function');
    }

    const result = data[0];
    
    console.log('[OpenLibraryReset] Reset completed:', result);
    
    toast.success(`Phase 2 Complete: Marked ${result.updated_count.toLocaleString()} books for re-enrichment!`, {
      description: `These books will now be enriched with ISBNdb for better quality data.`,
      duration: 6000
    });
    
    return {
      updated_count: result.updated_count,
      books_with_no_synopsis: result.books_with_no_synopsis,
      books_with_no_series: result.books_with_no_series,
      books_with_no_pages: result.books_with_no_pages
    };
  } catch (error) {
    console.error('[OpenLibraryReset] Failed:', error);
    toast.error('Failed to reset OpenLibrary enrichment status', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
    throw error;
  }
}

/**
 * Gets statistics about OpenLibrary books that need re-enrichment
 */
export async function getOpenLibraryEnrichmentStats() {
  try {
    // Get books from OpenLibrary with various data quality issues
    const { count: totalComplete, error: completeError } = await supabase
      .from('books')
      .select('*', { count: 'exact', head: true })
      .eq('source', 'openlibrary')
      .eq('enrichment_status', 'complete');

    if (completeError) throw completeError;

    const { count: missingData, error: missingError } = await supabase
      .from('books')
      .select('*', { count: 'exact', head: true })
      .eq('source', 'openlibrary')
      .eq('enrichment_status', 'complete')
      .or('synopsis.is.null,series_name.is.null,page_count.is.null');

    if (missingError) throw missingError;

    const { count: pendingCount, error: pendingError } = await supabase
      .from('books')
      .select('*', { count: 'exact', head: true })
      .eq('source', 'openlibrary')
      .eq('enrichment_status', 'pending');

    if (pendingError) throw pendingError;

    const { count: withISBN, error: isbnError } = await supabase
      .from('books')
      .select('*', { count: 'exact', head: true })
      .eq('source', 'openlibrary')
      .eq('enrichment_status', 'complete')
      .not('isbn13', 'is', null);

    if (isbnError) throw isbnError;

    return {
      totalComplete: totalComplete || 0,
      needsReEnrichment: missingData || 0,
      alreadyPending: pendingCount || 0,
      withISBN: withISBN || 0,
      canBenefitFromISBNdb: (withISBN || 0)
    };
  } catch (error) {
    console.error('[OpenLibraryStats] Failed to get stats:', error);
    throw error;
  }
}
